'use client';

import { useEffect } from 'react';

export default function ChunkErrorHandler() {
  useEffect(() => {
    const RELOAD_KEY = 'chunk_error_reload';
    const RELOAD_COUNT_KEY = 'chunk_error_count';

    const isChunkError = (error: unknown): boolean => {
      if (!error) return false;
      const e = error as { name?: string; message?: string };
      const name: string = e.name || '';
      const msg: string = e.message || String(error);
      return (
        name === 'ChunkLoadError' || msg.includes('Loading chunk') ||
        msg.includes('ChunkLoadError') ||
        msg.includes('app/layout') ||
        msg.includes('app-pages-internals') ||
        (msg.includes('timeout') && msg.includes('chunk'))
      );
    };

    const tryReload = () => {
      const count = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10);
      if (count >= 3) return; // Stop reload loop after 3 attempts
      const lastReload = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 8000) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
        // Hard reload with cache-busting query param to force fresh chunk fetch
        const url = window.location.href.split('?')[0] + '?_cb=' + now;
        window.location.href = url;
      }
    };

    const handleError = (event: ErrorEvent) => {
      if (isChunkError(event.error) || isChunkError({ message: event.message })) {
        event.preventDefault();
        tryReload();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkError(event.reason)) {
        event.preventDefault();
        tryReload();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Clear reload throttle on successful load so next session starts fresh
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_COUNT_KEY);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}
